import { useEffect, useMemo, useState } from 'react';
import './VisaTimeline.css';
import {
  PHASES,
  PREAPPROVAL_WINDOW,
  RANGES,
  START,
  STILL_AHEAD,
  TIMELINE,
  VISA_GRANTED_ON,
} from './visaTimelineData';

const DAY_MS = 24 * 60 * 60 * 1000;

// Whole calendar days between two dates, immune to DST shifts.
const daysBetween = (a, b) =>
  Math.round(
    (Date.UTC(b.getFullYear(), b.getMonth(), b.getDate()) - Date.UTC(a.getFullYear(), a.getMonth(), a.getDate())) /
      DAY_MS,
  );

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const sameDay = (a, b) => daysBetween(a, b) === 0;

const fmtShort = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' });
const fmtLong = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
const fmtWeekday = new Intl.DateTimeFormat('en-GB', { weekday: 'short' });
const fmtMonth = new Intl.DateTimeFormat('en-GB', { month: 'short' });
const pad = (n) => String(n).padStart(2, '0');

const KIND_LABEL = {
  milestone: 'Milestone',
  progress: 'Progress',
  ask: 'I chased it',
  stall: 'Stalled',
  holiday: 'Out of office',
  promise: 'A promise',
};

function useNow(intervalMs) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

function usePageChrome() {
  useEffect(() => {
    document.body.classList.add('visa-page');
    const prevTitle = document.title;
    document.title = 'Still Waiting — a Germany relocation timeline';
    // Shared privately: ask search engines not to index it.
    const robots = document.createElement('meta');
    robots.name = 'robots';
    robots.content = 'noindex, nofollow';
    document.head.appendChild(robots);
    return () => {
      document.body.classList.remove('visa-page');
      document.title = prevTitle;
      robots.remove();
    };
  }, []);
}

/* ---------- Hero ---------- */

function Hero({ now, end, done }) {
  const ms = Math.max(0, end - START);
  const days = Math.floor(ms / DAY_MS);
  const rest = ms - days * DAY_MS;
  const h = Math.floor(rest / 3600000);
  const m = Math.floor((rest % 3600000) / 60000);
  const s = Math.floor((rest % 60000) / 1000);

  return (
    <header className="pt-10 pb-12 md:pt-16 md:pb-16">
      <div className="vt-meta flex flex-wrap items-center justify-between gap-2 text-[11px] font-bold uppercase text-[color:var(--muted)]">
        <span>Antrag auf Umzug · Akte JV-2026-0707</span>
        <span>USA → Germany</span>
      </div>
      <div className="vt-flagrule mt-3" />

      <div className="relative mt-10 md:mt-14">
        <h1 className="max-w-3xl text-[2.1rem] font-extrabold leading-[1.02] tracking-[-0.03em] md:text-[3.6rem]">
          How long does it take to get a work visa to Germany?
        </h1>

        <div className="mt-8 flex flex-wrap items-end gap-x-5 gap-y-2 md:mt-10">
          <span className="vt-num text-[6.5rem] font-extrabold leading-[0.8] md:text-[11rem]">{days}</span>
          <div className="pb-2 md:pb-4">
            <div className="text-2xl font-extrabold md:text-4xl">days{done ? '.' : ' and counting.'}</div>
            {!done && (
              <div className="vt-num mt-1 text-sm font-semibold text-[color:var(--muted)] md:text-base">
                + {pad(h)}h {pad(m)}m {pad(s)}s
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <p className="max-w-2xl text-base leading-relaxed text-[color:var(--muted)] md:text-lg">
            On {fmtLong.format(START)} I asked my manager if I could relocate to our German office. Here is every step
            since, in order, with the gaps left in.
          </p>
          <div className={`vt-stamp ${done ? 'vt-stamp--done' : ''}`} aria-hidden="true">
            <span className="block text-[10px] tracking-[0.3em]">{done ? 'Genehmigt' : 'Noch in'}</span>
            <span className="block text-xl md:text-2xl">{done ? 'Approved' : 'Bearbeitung'}</span>
            <span className="block text-[10px] tracking-[0.2em]">{fmtShort.format(now)} 2026</span>
          </div>
        </div>

      </div>
    </header>
  );
}

/* ---------- Stats ---------- */

function Stats({ today }) {
  const people = new Set(TIMELINE.flatMap((e) => e.people || []));
  const firstHr = TIMELINE.find((e) => e.people.includes('Desiree')).date;
  const submitted = TIMELINE.find((e) => e.title === 'Submitted to Germany').date;
  const sigStart = TIMELINE.find((e) => e.signature === 1).date;
  const sigEnd = TIMELINE.find((e) => e.title.startsWith('Jan signs')).date;

  const stats = [
    { value: daysBetween(START, firstHr), unit: 'days', label: 'before HR got involved at all' },
    { value: TIMELINE.filter((e) => e.kind === 'holiday').length, unit: '×', label: 'two-week HR holidays mid-process' },
    { value: TIMELINE.filter((e) => e.kind === 'promise').length, unit: '×', label: '“it’ll be ready soon” that wasn’t' },
    { value: daysBetween(sigStart, sigEnd), unit: 'days', label: 'waiting on a single signature' },
    { value: daysBetween(START, submitted), unit: 'days', label: 'before anything reached Germany' },
    { value: people.size, unit: '', label: 'people and organizations involved' },
  ];

  return (
    <section aria-label="By the numbers" className="grid grid-cols-2 border-l-2 border-t-2 border-[color:var(--ink)] md:grid-cols-3">
      {stats.map((s) => (
        <div key={s.label} className="border-b-2 border-r-2 border-[color:var(--ink)] bg-[color:var(--card)] p-4 md:p-6">
          <div className="flex items-baseline gap-1">
            <span className="vt-num text-4xl font-extrabold md:text-6xl">{s.value}</span>
            {s.unit && <span className="text-sm font-bold text-[color:var(--muted)] md:text-base">{s.unit}</span>}
          </div>
          <div className="mt-2 text-sm font-semibold leading-snug md:text-base">{s.label}</div>
        </div>
      ))}
      <div className="col-span-2 border-b-2 border-r-2 border-[color:var(--ink)] bg-[color:var(--ink)] p-4 text-[color:var(--paper)] md:col-span-3 md:p-5">
        <span className="vt-num text-2xl font-extrabold md:text-3xl">{VISA_GRANTED_ON ? 1 : 0}</span>
        <span className="ml-3 text-sm font-semibold md:text-base">
          visas issued so far{VISA_GRANTED_ON ? '' : ` · day ${daysBetween(START, today) + 1}`}
        </span>
      </div>
    </section>
  );
}

/* ---------- Where the time went ---------- */

function PhaseBar({ end }) {
  const total = Math.max(1, daysBetween(START, end) + 1);
  const phases = PHASES.map((p, i) => {
    const next = PHASES[i + 1]?.start;
    const stop = next ? daysBetween(START, next) : total;
    const days = Math.max(0, stop - daysBetween(START, p.start));
    return { ...p, days, pct: (days / total) * 100 };
  });

  return (
    <div>
      <div className="flex h-12 w-full overflow-hidden border-2 border-[color:var(--ink)] md:h-14">
        {phases.map((p) => (
          <div
            key={p.key}
            className={`vt-phase vt-phase--${p.key} flex items-center justify-center border-r-2 border-[color:var(--ink)] last:border-r-0`}
            style={{ width: `${p.pct}%` }}
            title={`${p.label}: ${p.days} days`}
          >
            {p.pct > 9 && <span className="vt-num text-sm font-extrabold md:text-base">{p.days}d</span>}
          </div>
        ))}
      </div>
      <ul className="mt-4 grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2 md:grid-cols-3">
        {phases.map((p) => (
          <li key={p.key} className="flex items-center gap-2">
            <span className={`vt-phase vt-phase--${p.key} inline-block h-3.5 w-3.5 shrink-0 border-2 border-[color:var(--ink)]`} />
            <span className="font-semibold">{p.label}</span>
            <span className="vt-num ml-auto text-[color:var(--muted)] sm:ml-0">{p.days} days</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function stateFor(date, end) {
  let state = 'quiet';
  for (const r of RANGES) {
    const to = r.to || end;
    if (daysBetween(r.from, date) >= 0 && daysBetween(date, to) >= 0) {
      // Signature beats holiday where they overlap — it was the actual blocker.
      if (state === 'quiet' || r.kind === 'signature') state = r.kind;
    }
  }
  return state;
}

function DayGrid({ today, end }) {
  const { weeks, months } = useMemo(() => {
    const first = new Date(START);
    first.setDate(first.getDate() - ((first.getDay() + 6) % 7)); // back to Monday
    const last = new Date(end);
    last.setDate(last.getDate() + (6 - ((last.getDay() + 6) % 7))); // forward to Sunday

    const eventDays = new Set(TIMELINE.map((e) => daysBetween(START, e.date)));
    const weeks = [];
    const months = [];
    for (let cur = new Date(first); daysBetween(cur, last) >= 0; ) {
      const week = [];
      for (let i = 0; i < 7; i++) {
        const day = new Date(cur);
        const offset = daysBetween(START, day);
        const inRange = offset >= 0 && daysBetween(day, end) >= 0;
        week.push({
          date: day,
          inRange,
          event: inRange && eventDays.has(offset),
          state: inRange ? stateFor(day, end) : 'none',
          isToday: sameDay(day, today),
        });
        if (day.getDate() === 1 || (weeks.length === 0 && i === 0)) {
          months.push({ col: weeks.length, label: fmtMonth.format(day.getDate() === 1 ? day : START) });
        }
        cur.setDate(cur.getDate() + 1);
      }
      weeks.push(week);
    }
    return { weeks, months };
  }, [today, end]);

  const legend = [
    ['quiet', 'Nothing happening'],
    ['holiday', 'HR on holiday'],
    ['signature', 'Waiting on one signature'],
    ['gov', 'With the German government'],
  ];

  return (
    <div>
      <div className="overflow-x-auto pb-2">
        <div className="inline-block">
          <div className="relative ml-8 h-5">
            {months.map((mo) => (
              <span
                key={`${mo.label}-${mo.col}`}
                className="vt-meta absolute text-[10px] font-bold uppercase text-[color:var(--muted)]"
                style={{ left: `calc(${mo.col} * var(--cell-step))` }}
              >
                {mo.label}
              </span>
            ))}
          </div>
          <div className="flex">
            <div className="vt-grid-days mr-2 flex w-6 flex-col text-[10px] font-semibold text-[color:var(--muted)]">
              {['Mon', '', 'Wed', '', 'Fri', '', 'Sun'].map((l, i) => (
                <span key={i} className="vt-cell-h flex items-center">{l}</span>
              ))}
            </div>
            <div className="vt-grid flex">
              {weeks.map((week, wi) => (
                <div key={wi} className="flex flex-col">
                  {week.map((cell) => (
                    <span
                      key={cell.date.toISOString()}
                      className={[
                        'vt-cell',
                        `vt-cell--${cell.state}`,
                        cell.event ? 'vt-cell--event' : '',
                        cell.isToday ? 'vt-cell--today' : '',
                      ].join(' ')}
                      title={cell.inRange ? `${fmtWeekday.format(cell.date)} ${fmtShort.format(cell.date)}` : undefined}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <ul className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-xs font-semibold md:text-sm">
        {legend.map(([k, label]) => (
          <li key={k} className="flex items-center gap-2">
            <span className={`vt-cell vt-cell--${k} !m-0`} /> {label}
          </li>
        ))}
        <li className="flex items-center gap-2">
          <span className="vt-cell vt-cell--quiet vt-cell--event !m-0" /> Something actually happened
        </li>
      </ul>
    </div>
  );
}

/* ---------- Timeline ---------- */

function Gap({ days, reason }) {
  if (days < 4) return <div className="h-3 md:h-4" />;
  const height = Math.min(days * 7, 220);
  return (
    <div className="vt-gap relative ml-[7px] flex items-center pl-8 md:ml-[9px] md:pl-12" style={{ minHeight: height }}>
      <div className="py-3">
        <span className="vt-num text-lg font-extrabold md:text-xl">{days} days</span>
        <span className="ml-2 text-sm text-[color:var(--muted)]">{reason}</span>
      </div>
    </div>
  );
}

function Entry({ entry, index }) {
  const sigStart = TIMELINE.find((e) => e.signature === 1)?.date;
  return (
    <li className="relative flex gap-4 md:gap-8">
      <div className="relative z-10 flex flex-col items-center">
        <span className={`vt-dot vt-dot--${entry.kind}`} />
      </div>
      <article className={`vt-card vt-card--${entry.kind} -mt-1.5 w-full min-w-0 p-4 md:p-5`}>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="vt-num text-sm font-extrabold">
            {fmtWeekday.format(entry.date)} {fmtShort.format(entry.date)}
          </span>
          <span className="vt-meta text-[10px] font-bold uppercase text-[color:var(--muted)]">
            Day {daysBetween(START, entry.date) + 1}
          </span>
          <span className={`vt-badge vt-badge--${entry.kind} ml-auto`}>{KIND_LABEL[entry.kind]}</span>
        </div>
        <h3 className="mt-2 text-lg font-extrabold leading-snug md:text-xl">{entry.title}</h3>
        <p className="mt-1.5 text-[15px] leading-relaxed text-[color:var(--ink-soft)] md:text-base">{entry.text}</p>
        {entry.signature && sigStart && (
          <div className="vt-num mt-3 inline-block bg-[color:var(--gold)] px-2 py-0.5 text-xs font-extrabold text-[color:var(--ink)]">
            Unsigned for {daysBetween(sigStart, entry.date) + 1} day{daysBetween(sigStart, entry.date) ? 's' : ''}
          </div>
        )}
        <span className="sr-only">Entry {index + 1}</span>
      </article>
    </li>
  );
}

function gapReason(prev) {
  if (prev.kind === 'holiday') return '— HR on holiday';
  if (prev.title === 'Submitted to Germany') return '— waiting on the Bundesagentur';
  if (prev.kind === 'promise') return '— waiting on the promise';
  return 'of silence';
}

function Timeline({ today, done }) {
  const sinceLast = daysBetween(TIMELINE[TIMELINE.length - 1].date, today);
  return (
    <div className="vt-timeline relative">
      <ol className="m-0 list-none p-0">
        {TIMELINE.map((entry, i) => {
          const prev = TIMELINE[i - 1];
          return (
            <div key={`${entry.date.toISOString()}-${entry.title}`}>
              {prev && <Gap days={daysBetween(prev.date, entry.date)} reason={gapReason(prev)} />}
              <Entry entry={entry} index={i} />
            </div>
          );
        })}
      </ol>

      {!done && (
        <>
          <Gap days={sinceLast} reason="since the last update" />
          <div className="h-4" />
          <div className="relative flex gap-4 md:gap-8">
            <div className="relative z-10 flex flex-col items-center">
              <span className="vt-dot vt-dot--now" />
            </div>
            <div className="-mt-1.5 w-full border-2 border-dashed border-[color:var(--red)] p-4 md:p-5">
              <div className="flex items-center gap-2">
                <span className="vt-live inline-block h-2 w-2 rounded-full bg-[color:var(--red)]" />
                <span className="vt-meta text-[10px] font-bold uppercase text-[color:var(--red)]">Today · {fmtShort.format(today)}</span>
              </div>
              <h3 className="mt-2 text-lg font-extrabold md:text-xl">Still waiting.</h3>
              <PreapprovalWindow today={today} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function PreapprovalWindow({ today }) {
  const { from, to } = PREAPPROVAL_WINDOW;
  const span = daysBetween(from, to);
  const pos = Math.min(100, Math.max(0, (daysBetween(from, today) / span) * 100));
  const overdue = daysBetween(to, today) > 0;
  return (
    <div className="mt-3">
      <p className="text-[15px] leading-relaxed text-[color:var(--ink-soft)]">
        Pre-approval was quoted at 1–3 weeks from submission.{' '}
        {overdue
          ? `That window closed ${daysBetween(to, today)} days ago.`
          : `Expected between ${fmtShort.format(from)} and ${fmtShort.format(to)}.`}
      </p>
      <div className="relative mt-4 h-3 border-2 border-[color:var(--ink)] bg-[color:var(--card)]">
        <div className="absolute inset-y-0 left-0 bg-[color:var(--gold)]" style={{ width: `${pos}%` }} />
      </div>
      <div className="vt-num mt-1.5 flex justify-between text-xs font-semibold text-[color:var(--muted)]">
        <span>{fmtShort.format(from)}</span>
        <span>{fmtShort.format(to)}</span>
      </div>
    </div>
  );
}

function StillAhead() {
  return (
    <ol className="m-0 list-none space-y-0 p-0">
      {STILL_AHEAD.map((step, i) => (
        <li key={step} className="flex items-center gap-4 border-b-2 border-dashed border-[color:var(--rule)] py-3.5">
          <span className="vt-num flex h-8 w-8 shrink-0 items-center justify-center border-2 border-[color:var(--ink)] text-sm font-extrabold">
            {i + 1}
          </span>
          <span className="text-base font-semibold md:text-lg">{step}</span>
          <span className="vt-meta ml-auto text-[10px] font-bold uppercase text-[color:var(--muted)]">Pending</span>
        </li>
      ))}
    </ol>
  );
}

/* ---------- Page ---------- */

function SectionHeading({ kicker, title }) {
  return (
    <div className="mb-6 md:mb-8">
      <div className="vt-meta mb-2 text-[11px] font-bold uppercase text-[color:var(--muted)]">{kicker}</div>
      <h2 className="text-2xl font-extrabold tracking-tight md:text-3xl">{title}</h2>
    </div>
  );
}

function VisaTimeline() {
  usePageChrome();
  const now = useNow(1000);
  const done = Boolean(VISA_GRANTED_ON);
  const end = done ? VISA_GRANTED_ON : now;
  const today = startOfDay(end);

  return (
    <div className="vt min-h-screen w-full px-4 pb-16 md:px-10">
      <div className="mx-auto max-w-4xl">
        <Hero now={now} end={end} done={done} />

        <Stats today={today} />

        <section className="mt-16 md:mt-24">
          <SectionHeading kicker="Where the time went" title="Every day since the ask" />
          <PhaseBar end={today} />
          <div className="mt-10">
            <DayGrid today={today} end={today} />
          </div>
        </section>

        <section className="mt-16 md:mt-24">
          <SectionHeading kicker="The full record" title="Timeline" />
          <p className="-mt-3 mb-8 max-w-2xl text-sm text-[color:var(--muted)] md:text-base">
            Spacing between entries is to scale: the longer the gap, the longer nobody did anything.
          </p>
          <Timeline today={today} done={done} />
        </section>

        {!done && (
          <section className="mt-16 md:mt-24">
            <SectionHeading kicker="Still to come" title="What’s left before I can move" />
            <StillAhead />
          </section>
        )}

        <footer className="mt-16 md:mt-20">
          <div className="vt-flagrule" />
          <p className="vt-meta mt-4 text-[11px] font-bold uppercase text-[color:var(--muted)]">
            Last updated {fmtLong.format(TIMELINE[TIMELINE.length - 1].date)}
          </p>
        </footer>
      </div>
    </div>
  );
}

export default VisaTimeline;
